<template>
  <v-container class="slides">
    <v-card flat color="transparent" dark>
      <div class="d-flex justify-space-between align-center mb-4">
        <div>
          <v-card-title class="white--text text-h5 font-weight-bold">Slide Manager</v-card-title>
          <v-card-subtitle class="white--text"> Verwalte hier die Slides, die in der App angezeigt werden. </v-card-subtitle>
        </div>
        <v-btn color="primary" class="mr-md-4" @click="createNewSlide">
          <v-icon left>mdi-plus</v-icon>
          Neue Slide
        </v-btn>
      </div>

      <div v-if="sortedSlides.active.length > 0 || sortedSlides.upcoming.length > 0">
        <div class="section-title">Aktiv & Geplant</div>
        <v-row>
          <v-col v-for="item in [...sortedSlides.active, ...sortedSlides.upcoming]" :key="item._id" cols="12" sm="6" md="4">
            <v-card dark @click="editSlide(item)" class="item-card d-flex flex-column" min-height="350" hover>
              <div class="status-indicator" :class="item.status.colorClass">
                <v-icon small color="white">{{ item.status.icon }}</v-icon>
                <span class="ml-1 font-weight-bold">{{ item.status.text }}</span>
              </div>
              <v-img v-if="item.image" :src="'/img/' + item.image" height="180" class="white--text align-end">
                <v-card-title class="slide-title-on-image">{{ trimLength(item.title, 30) }}</v-card-title>
              </v-img>
              <div v-else class="grey darken-3 d-flex align-center justify-center" style="height: 180px">
                <v-icon size="50" color="grey">mdi-image-off-outline</v-icon>
              </div>
              <v-card-text class="white--text font-weight-light flex-grow-1 pt-4">
                <v-chip v-for="studio in item.studios" :key="studio._id" small class="ma-1">{{ studio.name }}</v-chip>
              </v-card-text>
              <v-divider></v-divider>
              <v-card-subtitle class="caption text-center pa-2">
                <v-icon small>mdi-calendar-range</v-icon>
                {{ $moment(item.showAfter).format("DD.MM.YY") }} - {{ $moment(item.hideAfter).format("DD.MM.YY") }}
              </v-card-subtitle>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <div v-if="sortedSlides.past.length > 0" class="mt-10">
        <div class="section-title">Archiv</div>
        <v-row>
          <v-col v-for="item in paginatedArchive" :key="item._id" cols="12" sm="6" md="4">
            <v-card dark @click="editSlide(item)" class="item-card is-archived d-flex flex-column" min-height="350" hover>
              <div class="status-indicator is-past">
                <v-icon small color="white">mdi-archive-outline</v-icon>
                <span class="ml-1 font-weight-bold">Archiviert</span>
              </div>
              <v-img v-if="item.image" :src="'/img/' + item.image" height="180"></v-img>
              <v-card-title class="pt-4">{{ trimLength(item.title, 30) }}</v-card-title>
            </v-card>
          </v-col>
        </v-row>
        <v-pagination v-if="archivePageCount > 1" v-model="archivePage" :length="archivePageCount" class="mt-4" dark></v-pagination>
      </div>
      <v-alert v-if="!slides || slides.length === 0" type="info" class="mt-4" text dark> Es sind noch keine Slides erstellt worden. </v-alert>
    </v-card>

    <v-dialog v-model="showEditDialog" max-width="900px" persistent>
      <v-card dark v-if="slide" class="mx-auto">
        <v-card-title class="text-h5">Slide {{ slide._id ? "bearbeiten" : "erstellen" }}</v-card-title>
        <v-card-text class="pt-4">
          <v-container>
            <v-row>
              <v-col cols="12" md="7">
                <v-text-field v-model="slide.title" label="Titel" placeholder="z.B. Sommer-Aktion" outlined></v-text-field>
                <v-text-field v-model="slide.link" label="Link (URL)" placeholder="https://..." outlined></v-text-field>
                <v-select
                  v-model="slide.studios"
                  :items="studios"
                  item-text="name"
                  item-value="_id"
                  label="Für welche Studios?"
                  multiple
                  chips
                  outlined
                  attach></v-select>
              </v-col>
              <v-col cols="12" md="5">
                <slim-cropper :key="slide.image" style="width: 100%" :options="slimOptionsExercise">
                  <img v-if="slide.image" :src="'/img/' + slide.image" alt="" />
                  <input type="file" accept="image/*" />
                </slim-cropper>
                <v-card-subtitle class="text-center">Format: 3:1 (900x300)</v-card-subtitle>
                <v-divider class="my-4"></v-divider>
                <v-date-picker v-model="datePicker" locale="de-DE" range no-title dark></v-date-picker>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showEditDialog = false">Abbrechen</v-btn>
          <v-btn color="primary" @click="saveSlide" :disabled="!isFormValid" :loading="working">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Slim from "../../../plugins/slimImage/slim/slim.vue"
import api from "../../../api"

export default {
  name: "slideManager",
  components: { "slim-cropper": Slim },
  data() {
    return {
      studios: [],
      working: false,
      showEditDialog: false,
      datePicker: [],
      slides: [],
      slide: null,
      archivePage: 1,
      archiveItemsPerPage: 8,
      slimOptionsExercise: {
        ratio: "3:1",
        initialImage: "/img/einmalig/imageUploadPlaceholder.jpg",
        buttonCancelLabel: "Abbrechen",
        buttonConfirmLabel: "Bestätigen",
        label: "Bild hochladen",
        service: this.uploadPicture,
        serviceFormat: "file",
        instantEdit: true,
        push: true,
      },
    }
  },
  computed: {
    sortedSlides() {
      const categories = { active: [], upcoming: [], past: [] }
      if (!this.slides) return categories

      const sortedByDate = [...this.slides].sort((a, b) => new Date(b.showAfter) - new Date(a.showAfter))

      sortedByDate.forEach((item) => {
        // --- This is the corrected logic ---

        // 1. Check for invalid dates first. This is good practice.
        if (!this.$moment(item.showAfter).isValid() || !this.$moment(item.hideAfter).isValid()) {
          categories["past"].push({ ...item, status: this.getStatusDetails("past") })
          return // Skip to the next item
        }

        const now = this.$moment()
        const from = this.$moment(item.showAfter)
        // Set the 'to' date to the very end of the day to make it inclusive.
        const to = this.$moment(item.hideAfter).endOf("day")
        let status

        // 2. Use a clearer structure and the 'day' granularity for comparison.
        if (now.isBetween(from, to)) {
          // isBetween is perfect for checking if something is currently active.
          status = "active"
        } else if (now.isAfter(to)) {
          // Use isAfter for things that have clearly ended.
          status = "past"
        } else {
          // Everything else must be upcoming.
          status = "upcoming"
        }

        categories[status].push({ ...item, status: this.getStatusDetails(status, from) })
      })
      return categories
    },
    paginatedArchive() {
      const start = (this.archivePage - 1) * this.archiveItemsPerPage
      const end = start + this.archiveItemsPerPage
      return this.sortedSlides.past.slice(start, end)
    },
    archivePageCount() {
      return Math.ceil(this.sortedSlides.past.length / this.archiveItemsPerPage)
    },
    isFormValid() {
      if (!this.slide) return false
      const { title, studios, showAfter, hideAfter, image } = this.slide
      return title && title.length > 2 && studios && studios.length > 0 && showAfter && hideAfter && image
    },
  },
  created() {
    this.fetchSlides()
    this.initSlide()
  },
  methods: {
    initSlide() {
      this.datePicker = []
      this.slide = {
        _id: null,
        hideAfter: null,
        showAfter: null,
        link: "",
        image: "",
        title: "",
        studios: [],
      }
    },
    getStatusDetails(status, from) {
      const now = this.$moment()
      switch (status) {
        case "active":
          return { text: "Aktiv", icon: "mdi-broadcast", colorClass: "is-visible" }
        case "upcoming":
          const diff = from.diff(now, "days") + 1
          return { text: `Startet in ${diff} Tag(en)`, icon: "mdi-clock-start", colorClass: "is-upcoming" }
        default:
          return { text: "Archiviert", icon: "mdi-archive-outline", colorClass: "is-past" }
      }
    },
    editSlide(slide) {
      this.slide = JSON.parse(JSON.stringify(slide))
      this.slide.studios = this.slide.studios.map((s) => s._id || s)

      // HIER IST DER FIX: Sicherstellen, dass die Daten gültig sind, bevor sie dem Picker zugewiesen werden
      if (this.slide.showAfter && this.slide.hideAfter) {
        this.datePicker = [this.$moment(this.slide.showAfter).format("YYYY-MM-DD"), this.$moment(this.slide.hideAfter).format("YYYY-MM-DD")]
      } else {
        this.datePicker = []
      }
      this.showEditDialog = true
    },
    createNewSlide() {
      this.initSlide()
      this.showEditDialog = true
    },
    async fetchSlides() {
      try {
        let res = await api.fetchSlides()
        this.studios = res.studios
        this.slides = res.slides
        this.$emit("finishedloading")
      } catch (e) {
        this.$toast.error("Fehler beim Laden der Slides.")
      }
    },
    async saveSlide() {
      if (!this.isFormValid) {
        this.$toast.error("Bitte fülle alle Felder aus (Titel, Bild, Datum, Studios).")
        return
      }
      this.working = true
      try {
        await api.saveSlide(this.slide)
        this.$toast.success("Slide erfolgreich gespeichert.")
        this.showEditDialog = false
        this.fetchSlides()
      } catch (e) {
        this.$toast.error("Fehler beim Speichern der Slide.")
      } finally {
        this.working = false
      }
    },
    uploadPicture(formdata, progress, success, failure) {
      const formData = new FormData()
      formData.append("file", formdata[0])
      api
        .uploadImage({ art: "slide", formData })
        .then((data) => {
          if (data.status) {
            this.slide.image = data.name
            success(data)
          }
        })
        .catch((error) => failure(error))
    },
    trimLength(str, length) {
      if (!str) return ""
      return str.length > length ? str.substring(0, length) + "..." : str
    },
  },
  watch: {
    datePicker(value) {
      if (!this.slide) return

      // HIER IST DER FIX: Sicherstellen, dass die Werte gesetzt werden oder null sind
      if (value && value.length === 2) {
        const sorted = [...value].sort((a, b) => new Date(a) - new Date(b))
        this.slide.showAfter = sorted[0]
        this.slide.hideAfter = sorted[1]
      } else {
        // Setzt die Daten zurück, wenn der Picker geleert wird
        this.slide.showAfter = null
        this.slide.hideAfter = null
      }
    },
  },
}
</script>

<style scoped>
.item-card {
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border-radius: 12px !important;
  background: linear-gradient(145deg, #2a2a2a, #1e1e1e);
  box-shadow: 5px 5px 15px #1c1c1c, -5px -5px 15px #2e2e2e;
  border: 2px solid transparent;
  position: relative;
  height: 100%;
}
.item-card:hover {
  transform: translateY(-5px);
  border-color: var(--v-primary-base);
}
.item-card.is-archived {
  opacity: 0.7;
}
.slide-title-on-image {
  font-size: 1.1rem;
  line-height: 1.3;
  text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.8);
}
.status-indicator {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  color: white;
  z-index: 1;
}
.status-indicator.is-visible {
  background-color: #4caf50;
}
.status-indicator.is-upcoming {
  background-color: #ff9800;
}
.status-indicator.is-past {
  background-color: #616161;
}

.section-title {
  font-size: 1.5rem;
  font-weight: bold;
  color: white;
  margin-bottom: 16px;
  margin-top: 16px;
  padding-left: 8px;
  border-left: 4px solid var(--v-primary-base);
}
</style>
